<script>
import classModifierMixin from './mixins/class-modifier-mixin'
import BaseContainer from './BaseContainer'
import BaseCard from './BaseCard'
import BaseLoading from './BaseLoading'

export default {
	name: 'BaseAccordion',
	mixins: [classModifierMixin],
	components: {
		BaseContainer,
		BaseCard,
		BaseLoading
	},
	props: {
		numberOfTabs: {
			type: Number,
			default: 1
		},
		header: {
			type: Boolean,
			default: false
		},
		tabOptions: {
			type: Array,
			default: () => ({
				id: 1,
				title: '',
				defaultState: 'COLLAPSE',
				scrollable: true,
				icon: []
			})
		}
	},
	data: () => ({
		loading: true,
		tabState: [{ id: 0, state: 'COLLAPSE' }],
		autoMode: true,
		activeTab: 0,
		tabs: []
	}),
	beforeDestroy: function () {
		window.removeEventListener('resize', this.isScrollVisible)
	},
	mounted: function () {
		this.tabState = this.tabOptions.map((t, index) => {
			return { id: index, state: t.defaultState }
		})
		this.loading = false
		this.autoMode = false
		this.$nextTick(function () {
			// need to wait till all child slots have finished loading into the view
			this.tabs = document.querySelectorAll('.base-accordion .base-accordion__tab--content > div')
			this.tabs.forEach(tab => {
					tab.addEventListener('scroll', this.handleScroll)
				})
		})
    this.isScrollVisible()
    window.addEventListener('resize', this.isScrollVisible)
	},
	methods: {
		setTabState: function (event, id) {
			// set BaseCard to auto mode to auto-open next tab on a card collapsed event
			// or to auto-close all other tabs on a card expanded event
			this.autoMode = true
			if (event && event.cardCollapsed) {
				this.tabState.forEach((t) => {
					if ((t.id === (id + 1)) || (id === (this.tabState.length - 1) && t.id === 0)) {
						t.state = 'EXPAND'
						this.activeTab = t.id
					} else {
						t.state = 'COLLAPSE'
					}
				})
			} else if (event && !event.cardCollapsed) {
				this.activeTab = id
				this.tabState.forEach((t) => {
					if (t.id === id) {
						t.state = 'EXPAND'
					} else {
						t.state = 'COLLAPSE'
					}
				})
			}
			this.$nextTick(() => {
				this.autoMode = false
			})
      this.isScrollVisible()
		},
		setIconState: function (event, id) {
			if (event && event.iconClicked) {
				this.$emit('icon-clicked', id)
			}
		},
		showIcon: function (id) {
			const tab = this.tabOptions[id]
			return (tab.scrolledToEnd) ? tab.icon : []
		},
		handleScroll: function (event) {
			const element = event.target
			// Less strict calculation to accommodate different devices/browsers
			if (Math.round(element.scrollHeight - element.scrollTop) <= Math.round(element.clientHeight * 1.01)) {
				this.$emit('scroll-to-end', element.dataset.tabId)
			}
		},
		isScrollVisible: function () {
			this.$nextTick(() => {
				let tabId = 0
				this.tabs.forEach((tab, i) => {
					if (tab && tab.scrollHeight !== 0 && tab.scrollHeight <= tab.offsetHeight) {
						tabId = tab.dataset.tabId
						this.$emit('tab-not-scrollable', tabId)
					}
				})
			})
		}
	}
}
</script>
<template>
	<div class="base-accordion" :class="modifierClasses" v-on="$listeners">
		<base-loading :loading="loading" modifiers="medium-spinner light-overlay">
			<div v-if="!loading">
				<div class="base-accordion__tab--container" v-for="(tab, index) in tabOptions" :key="index">
					<base-card class="base-accordion__tab"
							   :class="{'base-accordion__tab--active' : (activeTab === index)}"
							   :collapsible="true" :header="true" :auto-mode="autoMode"
							   :auto-state="tabState[index].state" :ignoreIconClick="false"
							   :card-icon="showIcon(index)" @icon-clicked="setIconState($event, tab.id)"
							   @card-collapsed="setTabState($event, index)">
						<div class="base-accordion__tab--header" slot="header" v-html="tab.heading"></div>
						<base-container class="base-accordion__tab--content"
										:class="{ 'base-accordion__tab--scrollable' : tab.scrollable }" slot="content">
							<slot name="tab" :tab="tab"/>
						</base-container>
					</base-card>
				</div>
			</div>
		</base-loading>
	</div>
</template>
<style lang="scss" scoped>
.base-accordion {
	display: flex;
	flex-direction: column;
	&--border {
		border: 1px solid var(--border-colour);
	}
	&__tab {
		background-color: var(--background-highlight-colour);
		color: var(--on-background);
		padding: 0 0;
		margin: 0 0;
		border-radius: 0;
		transition: all 0.5s ease-in-out;
		&--container {
			max-width: 100%;
		}
		&--header {
			max-width: 100%;
			height: 100%;
			border-radius: 0;
			padding: 0.5rem 0.5rem;
            font-size: var(--font-size-body-2);
			font-weight: var(--font-weight-body-2);
		}
		&--active {
			background-color: var(--background-variant-dark);
			color: var(--on-background);
		}
		&--content {
			transition: all 0.25s ease-in-out;
			background-color: white;
			margin: 0;
			border-top: 0.5px solid var(--border-colour);
		}
		&--scrollable {
			overflow: scroll;
		}
	}
}

</style>
